/**
 * Estimator Context
 *
 * Global state management for the estimator wizard.
 * Manages wizard steps, user inputs, and calculation results.
 */

import React, { createContext, useContext, useReducer, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useCurrency } from '../hooks/useCurrency';
import {
  WizardStep,
  WizardState,
  ProductColor,
  PanelOrientation,
  EstimateInput,
  EstimateOutput,
  EstimateDesigner,
  EstimatePreview,
  Currency,
  CeilingZone,
  WallConfig,
  ProjectInfo,
} from '../types';
import { doEstimate } from '../calculations';
import { withDesignerMaterials, wizardSeedFromPlan } from '../calculations/plan';
import { trackDesignerEstimate } from '../utils/gtmTracking';
import { maybeRefreshProducts, getLoadedRetailerId } from '../data/productTable';
import { syncEstimateData, registerStateLoader, loadSessionSnapshot, clearSessionSnapshot } from '../services/estimateDataManager';
import { patchAuthEstimateData } from '../services/api';
import { buildWizardSnapshot } from '../utils/estimateSnapshot';
// Validation toasts are built here, outside any component, so they use the
// standalone accessor. They are produced by a click handler and shown
// immediately, which is exactly the call pattern tr() documents as safe.
import { tr } from '../i18n/standalone';
import { clearAdvancedState } from '../components/designer/advanced/advancedStorage';

// ============================================================================
// Types
// ============================================================================

interface EstimatorState extends WizardState {
  estimateResult: EstimateOutput | null;
  isCalculating: boolean;
  calculationError: string | null;
  currency: Currency;
  /**
   * Set once a logged-in user names+saves the estimate from the results page.
   * Persists across "back to edit" + recalculation so we don't re-prompt for a
   * name, and carries the DB guid for renames. Cleared by RESET / Start Over.
   */
  savedEstimate: { guid: string; name: string; projectType?: string } | null;
}

type EstimatorAction =
  | { type: 'SET_STEP'; step: WizardStep }
  | { type: 'COMPLETE_STEP'; step: WizardStep }
  | { type: 'UPDATE_WALLS_CONFIG'; config: Partial<WizardState['wallsConfig']> }
  | { type: 'UPDATE_CEILING_CONFIG'; config: Partial<WizardState['ceilingConfig']> }
  | { type: 'UPDATE_DIMENSIONS_CONFIG'; config: Partial<WizardState['dimensionsConfig']> }
  | { type: 'UPDATE_APPLICATION_CONFIG'; config: Partial<WizardState['applicationConfig']> }
  | { type: 'UPDATE_PROJECT_INFO'; info: Partial<ProjectInfo> }
  | { type: 'SET_INDIVIDUAL_WALLS'; walls: WallConfig[] }
  | { type: 'ADD_INDIVIDUAL_WALL' }
  | { type: 'COPY_INDIVIDUAL_WALL'; index: number }
  | { type: 'UPDATE_INDIVIDUAL_WALL'; index: number; wall: Partial<WallConfig> }
  | { type: 'REMOVE_INDIVIDUAL_WALL'; index: number }
  | { type: 'SET_CALCULATION_MODE'; mode: WizardState['calculationMode'] }
  | { type: 'ADD_CEILING_ZONE' }
  | { type: 'UPDATE_CEILING_ZONE'; index: number; zone: Partial<CeilingZone> }
  | { type: 'REMOVE_CEILING_ZONE'; index: number }
  | { type: 'CALCULATE_START' }
  | { type: 'CALCULATE_SUCCESS'; result: EstimateOutput }
  | { type: 'CALCULATE_ERROR'; error: string }
  | { type: 'UPDATE_USER_NOTES'; notes: string }
  | { type: 'SET_CURRENCY'; currency: Currency }
  | { type: 'CLEAR_RESULTS' }
  | { type: 'SET_SAVED_ESTIMATE'; saved: { guid: string; name: string; projectType?: string } | null }
  | { type: 'RESET' }
  | { type: 'LOAD_STATE'; payload: LoadStatePayload }
  // An estimate made in the 3D designer: the wizard state read off its plan, and the result with it.
  | { type: 'LOAD_DESIGNER_RESULT'; payload: LoadStatePayload; result: EstimateOutput }
  // The preview still's data URL swapped for the saved picture's URL.
  | { type: 'SET_PREVIEW_IMAGE'; image: string };

/** Payload for restoring wizard state from a saved file */
export interface LoadStatePayload {
  calculationMode: WizardState['calculationMode'];
  projectInfo: ProjectInfo;
  wallsConfig: WizardState['wallsConfig'];
  ceilingConfig: WizardState['ceilingConfig'];
  dimensionsConfig: WizardState['dimensionsConfig'];
  applicationConfig: WizardState['applicationConfig'];
  individualWalls?: WallConfig[];
  currency: Currency;
  completedSteps: WizardStep[];
  currentStep: WizardStep;
}

interface EstimatorContextValue {
  state: EstimatorState;
  // Navigation
  goToStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  // Mode selection
  setCalculationMode: (mode: WizardState['calculationMode']) => void;
  // Configuration updates
  updateWallsConfig: (config: Partial<WizardState['wallsConfig']>) => void;
  updateCeilingConfig: (config: Partial<WizardState['ceilingConfig']>) => void;
  updateDimensionsConfig: (config: Partial<WizardState['dimensionsConfig']>) => void;
  updateApplicationConfig: (config: Partial<WizardState['applicationConfig']>) => void;
  updateProjectInfo: (info: Partial<ProjectInfo>) => void;
  // Individual walls
  setIndividualWalls: (walls: WallConfig[]) => void;
  addIndividualWall: () => void;
  copyIndividualWall: (index: number) => void;
  updateIndividualWall: (index: number, wall: Partial<WallConfig>) => void;
  removeIndividualWall: (index: number) => void;
  // Ceiling zones
  addCeilingZone: () => void;
  copyCeilingZone: (index: number) => void;
  updateCeilingZone: (index: number, zone: Partial<CeilingZone>) => void;
  removeCeilingZone: (index: number) => void;
  // Calculation
  calculateEstimate: () => void;
  updateUserNotes: (notes: string) => void;
  // Other
  setCurrency: (currency: Currency) => void;
  clearResults: () => void;
  /** Record (or clear) the saved-estimate guid+name for a logged-in user. */
  setSavedEstimate: (saved: { guid: string; name: string; projectType?: string } | null) => void;
  reset: () => void;
  /** Programmatic state hydration — used by "New Estimate from this template". */
  loadState: (payload: LoadStatePayload) => void;
  /** An estimate from the 3D designer: the plan's materials, a wizard state read off the plan, and the preview still. */
  loadDesignerEstimate: (args: { doc: EstimateDesigner; preview: EstimatePreview }) => Promise<void>;
  /** After a save: the preview still's data URL swapped for the saved picture's URL. */
  setPreviewImage: (image: string) => void;
  // Computed values
  canProceed: boolean;
  isComplete: boolean;
  getStepOrder: () => WizardStep[];
  getValidationErrors: () => ValidationError[];
  // Validation helpers
  validateSlatWall: (wallHeight: number, startHeight: number, numPanels: number) => number;
  validateGarageDoors: (wallLength: number, wallHeight: number, doorWidth: number, doorHeight: number, numDoors: number) => { validWidth: number; validHeight: number; validNumDoors: number };
}

// ============================================================================
// Initial State
// ============================================================================

// Step orders based on calculation mode
// NOTE: leadCapture step temporarily removed for testing
const STEP_ORDERS: Record<string, WizardStep[]> = {
  FullRoom: ['mode', 'walls', 'ceiling', 'dimensions', 'application'],
  CeilingOnly: ['mode', 'ceiling'],  // No dimensions step - zones have dimensions
  IndividualWalls: ['mode', 'walls', 'ceiling', 'application'],  // No dimensions step - individual walls have their own dimensions
};

// Default wall configuration for individual walls
const createDefaultWall = (index: number): WallConfig => ({
  wallId: `wall-${Date.now()}-${index}`,
  name: `Wall ${index + 1}`,
  enabled: true,
  length: 20,
  height: 10,
  color: 'White',
  orientation: 'Horizontal',
  slatWall: {
    enabled: false,
    startHeight: 0,
    numPanelRows: 0,
    color: 'Gray',
    useCapTrim: true,
  },
  garage: {
    enabled: false,
    width: 16,
    height: 7,
    numDoors: 1,
  },
});

// Default ceiling zone
const createDefaultZone = (index: number): CeilingZone => ({
  id: `zone-${Date.now()}-${index}`,
  name: index === 0 ? 'Main Area' : `Zone ${index + 1}`,
  length: 12,
  width: 20,
  color: 'White',
});

const initialState: EstimatorState = {
  currentStep: 'mode',
  completedSteps: [],
  calculationMode: 'FullRoom',

  // Project info (collected in lead capture)
  projectInfo: {
    projectName: '',
    customerName: '',
    email: '',
    phone: '',
    location: '',
  },

  wallsConfig: {
    color: 'White',
    orientation: 'Horizontal',
    slatWallStartHeight: 0,
    slatWallNumPanels: 0,
    slatWallColor: 'Gray',
    slatWallCoveragePercent: 0,
    usePresets: true,
  },

  ceilingConfig: {
    include: true,
    color: 'White',
    zones: undefined,
  },

  dimensionsConfig: {
    usePreset: true,
    presetId: 'single-car',
    customDimensions: undefined,
  },

  applicationConfig: {
    spaceType: 'garage',
    garage: {
      enabled: false,
      width: 9,  // Default to 9×7 (Single) which fits most room presets
      height: 7,
      numDoors: 1,
      onWallType: 'None',
    },
  },

  individualWalls: undefined,
  estimateResult: null,
  isCalculating: false,
  calculationError: null,
  currency: 'USD',
  savedEstimate: null,
};

// ============================================================================
// Validation Helpers
// ============================================================================

// Auto-adjust SlatWall panels if they exceed wall height
function validateSlatWallPanels(wallHeight: number, startHeight: number, numPanels: number): number {
  const panelHeight = 0.5; // 6 inches = 0.5 feet
  const maxPanels = Math.floor((wallHeight - startHeight) / panelHeight);
  return Math.min(numPanels, Math.max(0, maxPanels));
}

// Validate garage door dimensions don't exceed wall dimensions
function validateGarageDoorConfig(
  wallLength: number,
  wallHeight: number,
  doorWidth: number,
  doorHeight: number,
  numDoors: number
): { validWidth: number; validHeight: number; validNumDoors: number } {
  let validWidth = Math.min(doorWidth, wallLength);
  let validHeight = Math.min(doorHeight, wallHeight);
  let validNumDoors = numDoors;

  // Ensure combined door width doesn't exceed wall length
  const totalDoorWidth = validWidth * validNumDoors;
  if (totalDoorWidth > wallLength) {
    validNumDoors = Math.floor(wallLength / validWidth);
  }

  return { validWidth, validHeight, validNumDoors };
}

// ============================================================================
// Reducer
// ============================================================================

function estimatorReducer(state: EstimatorState, action: EstimatorAction): EstimatorState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.step };

    case 'COMPLETE_STEP':
      if (state.completedSteps.includes(action.step)) {
        return state;
      }
      return {
        ...state,
        completedSteps: [...state.completedSteps, action.step],
      };

    case 'UPDATE_WALLS_CONFIG':
      return {
        ...state,
        wallsConfig: { ...state.wallsConfig, ...action.config },
      };

    case 'UPDATE_CEILING_CONFIG': {
      const newCeilingConfig = { ...state.ceilingConfig, ...action.config };

      // Initialize zones when ceiling is enabled for IndividualWalls mode
      if (action.config.include === true &&
          state.calculationMode === 'IndividualWalls' &&
          (!newCeilingConfig.zones || newCeilingConfig.zones.length === 0)) {
        const zone = createDefaultZone(0);
        zone.color = newCeilingConfig.color;
        newCeilingConfig.zones = [zone];
      }

      // Sync top-level color change to first zone (zone 0)
      if (action.config.color && newCeilingConfig.zones && newCeilingConfig.zones.length > 0) {
        newCeilingConfig.zones = [
          { ...newCeilingConfig.zones[0], color: action.config.color },
          ...newCeilingConfig.zones.slice(1),
        ];
      }

      return {
        ...state,
        ceilingConfig: newCeilingConfig,
      };
    }

    case 'UPDATE_DIMENSIONS_CONFIG': {
      const newDimensionsConfig = { ...state.dimensionsConfig, ...action.config };

      // Get the new wall height based on updated dimensions
      let newWallHeight: number | undefined;
      if (newDimensionsConfig.customDimensions?.height) {
        newWallHeight = newDimensionsConfig.customDimensions.height;
      } else if (newDimensionsConfig.presetId) {
        const preset = ROOM_PRESETS.find(p => p.id === newDimensionsConfig.presetId);
        newWallHeight = preset?.height;
      }

      // Auto-recalculate SlatWall when wall height changes
      let updatedWallsConfig = state.wallsConfig;
      if (newWallHeight && state.wallsConfig.slatWallNumPanels > 0) {
        const coveragePercent = state.wallsConfig.slatWallCoveragePercent;

        if (coveragePercent && coveragePercent > 0) {
          // Design preset mode: recalculate from coverage percentage
          const recalculated = calculateSlatWallFromCoverage(coveragePercent, newWallHeight);
          updatedWallsConfig = {
            ...state.wallsConfig,
            slatWallStartHeight: recalculated.startHeight,
            slatWallNumPanels: recalculated.numPanelRows,
          };
        } else {
          // Custom config: clamp values to fit new wall height
          let validatedStartHeight = state.wallsConfig.slatWallStartHeight;
          if (validatedStartHeight >= newWallHeight) {
            validatedStartHeight = Math.max(0, newWallHeight * 0.5);
          }

          const validatedPanels = validateSlatWallPanels(
            newWallHeight,
            validatedStartHeight,
            state.wallsConfig.slatWallNumPanels
          );

          if (validatedPanels !== state.wallsConfig.slatWallNumPanels ||
              validatedStartHeight !== state.wallsConfig.slatWallStartHeight) {
            updatedWallsConfig = {
              ...state.wallsConfig,
              slatWallNumPanels: validatedPanels,
              slatWallStartHeight: validatedStartHeight,
            };
          }
        }
      }

      return {
        ...state,
        dimensionsConfig: newDimensionsConfig,
        wallsConfig: updatedWallsConfig,
      };
    }

    case 'UPDATE_APPLICATION_CONFIG':
      return {
        ...state,
        applicationConfig: { ...state.applicationConfig, ...action.config },
      };

    case 'UPDATE_PROJECT_INFO':
      return {
        ...state,
        projectInfo: { ...state.projectInfo, ...action.info },
      };

    case 'SET_INDIVIDUAL_WALLS':
      return {
        ...state,
        individualWalls: action.walls,
        calculationMode: 'IndividualWalls',
      };

    case 'ADD_INDIVIDUAL_WALL': {
      const currentWalls = state.individualWalls || [];
      const newWall = createDefaultWall(currentWalls.length);
      return {
        ...state,
        individualWalls: [...currentWalls, newWall],
      };
    }

    case 'COPY_INDIVIDUAL_WALL': {
      const currentWalls = state.individualWalls || [];
      if (action.index < 0 || action.index >= currentWalls.length) return state;
      const wallToCopy = currentWalls[action.index];
      const copiedWall: WallConfig = {
        ...wallToCopy,
        wallId: `wall-${Date.now()}`,
        name: `${wallToCopy.name} (Copy)`,
        slatWall: { ...wallToCopy.slatWall },
        garage: { ...wallToCopy.garage },
      };
      return {
        ...state,
        individualWalls: [...currentWalls, copiedWall],
      };
    }

    case 'UPDATE_INDIVIDUAL_WALL': {
      if (!state.individualWalls) return state;
      const updatedWalls = [...state.individualWalls];
      const currentWall = updatedWalls[action.index];
      const updatedWall = { ...currentWall, ...action.wall };

      // Auto-validate SlatWall if updating related fields
      if (action.wall.slatWall || action.wall.height) {
        const wallHeight = action.wall.height ?? currentWall.height;
        const slatWall = { ...currentWall.slatWall, ...action.wall.slatWall };
        slatWall.numPanelRows = validateSlatWallPanels(
          wallHeight,
          slatWall.startHeight,
          slatWall.numPanelRows
        );
        updatedWall.slatWall = slatWall;
      }

      // Auto-validate garage doors if updating related fields
      if (action.wall.garage || action.wall.length || action.wall.height) {
        const wallLength = action.wall.length ?? currentWall.length;
        const wallHeight = action.wall.height ?? currentWall.height;
        const garage = { ...currentWall.garage, ...action.wall.garage };
        const validated = validateGarageDoorConfig(
          wallLength,
          wallHeight,
          garage.width,
          garage.height,
          garage.numDoors
        );
        garage.width = validated.validWidth;
        garage.height = validated.validHeight;
        garage.numDoors = validated.validNumDoors;
        updatedWall.garage = garage;
      }

      updatedWalls[action.index] = updatedWall;
      return {
        ...state,
        individualWalls: updatedWalls,
      };
    }

    case 'REMOVE_INDIVIDUAL_WALL': {
      if (!state.individualWalls || state.individualWalls.length <= 1) return state;
      const filteredWalls = state.individualWalls.filter((_, i) => i !== action.index);
      return {
        ...state,
        individualWalls: filteredWalls,
      };
    }

    case 'SET_CALCULATION_MODE': {
      // When switching modes, initialize appropriate defaults
      let newIndividualWalls = state.individualWalls;
      let newCeilingZones = state.ceilingConfig.zones;

      if (action.mode === 'IndividualWalls' && (!state.individualWalls || state.individualWalls.length === 0)) {
        // Initialize with one default wall
        newIndividualWalls = [createDefaultWall(0)];
      }

      if (
        (action.mode === 'CeilingOnly' || (action.mode === 'IndividualWalls' && state.ceilingConfig.include)) &&
        (!newCeilingZones || newCeilingZones.length === 0)
      ) {
        // Initialize with one default zone for ceiling-only or individual walls mode
        const zone = createDefaultZone(0);
        zone.color = state.ceilingConfig.color;
        newCeilingZones = [zone];
      }

      return {
        ...state,
        calculationMode: action.mode,
        individualWalls: newIndividualWalls,
        ceilingConfig: {
          ...state.ceilingConfig,
          include: action.mode === 'CeilingOnly' ? true : state.ceilingConfig.include,
          zones: newCeilingZones,
        },
      };
    }

    case 'ADD_CEILING_ZONE': {
      const currentZones = state.ceilingConfig.zones || [];
      const newZone = createDefaultZone(currentZones.length);
      newZone.color = state.ceilingConfig.color;
      return {
        ...state,
        ceilingConfig: {
          ...state.ceilingConfig,
          zones: [...currentZones, newZone],
        },
      };
    }

    case 'UPDATE_CEILING_ZONE': {
      if (!state.ceilingConfig.zones) return state;
      const updatedZones = [...state.ceilingConfig.zones];
      updatedZones[action.index] = { ...updatedZones[action.index], ...action.zone };
      return {
        ...state,
        ceilingConfig: {
          ...state.ceilingConfig,
          zones: updatedZones,
        },
      };
    }

    case 'REMOVE_CEILING_ZONE': {
      if (!state.ceilingConfig.zones || state.ceilingConfig.zones.length <= 1) return state;
      const filteredZones = state.ceilingConfig.zones.filter((_, i) => i !== action.index);
      return {
        ...state,
        ceilingConfig: {
          ...state.ceilingConfig,
          zones: filteredZones,
        },
      };
    }

    case 'CALCULATE_START':
      return {
        ...state,
        isCalculating: true,
        calculationError: null,
      };

    case 'CALCULATE_SUCCESS':
      return {
        ...state,
        isCalculating: false,
        // Re-applying the saved guid keeps comments / reference id / rename
        // wired to the same DB record after an edit + recalculate.
        estimateResult: state.savedEstimate
          ? { ...action.result, id: state.savedEstimate.guid }
          : action.result,
        calculationError: null,
      };

    case 'CALCULATE_ERROR':
      return {
        ...state,
        isCalculating: false,
        calculationError: action.error,
      };

    case 'UPDATE_USER_NOTES':
      return {
        ...state,
        estimateResult: state.estimateResult
          ? { ...state.estimateResult, userNotes: action.notes }
          : null,
      };

    case 'SET_CURRENCY':
      return { ...state, currency: action.currency };

    case 'CLEAR_RESULTS':
      // Keep savedEstimate: "back to edit" must not re-trigger the name prompt.
      return {
        ...state,
        estimateResult: null,
        isCalculating: false,
        calculationError: null,
      };

    case 'SET_SAVED_ESTIMATE':
      return {
        ...state,
        savedEstimate: action.saved,
        // Mirror the guid onto the current result so reference id / comments
        // light up immediately on first save.
        estimateResult: state.estimateResult && action.saved
          ? { ...state.estimateResult, id: action.saved.guid }
          : state.estimateResult,
      };

    case 'RESET':
      return initialState;

    case 'LOAD_STATE': {
      const p = action.payload;
      return {
        ...initialState,
        currentStep: p.currentStep,
        completedSteps: [...p.completedSteps],
        calculationMode: p.calculationMode,
        projectInfo: { ...p.projectInfo },
        wallsConfig: { ...p.wallsConfig },
        ceilingConfig: { ...p.ceilingConfig },
        dimensionsConfig: { ...p.dimensionsConfig },
        applicationConfig: { ...p.applicationConfig },
        individualWalls: p.individualWalls
          ? p.individualWalls.map(w => ({ ...w, slatWall: { ...w.slatWall }, garage: { ...w.garage } }))
          : undefined,
        currency: p.currency,
        estimateResult: null,
        isCalculating: false,
        calculationError: null,
        savedEstimate: null,
      };
    }

    case 'LOAD_DESIGNER_RESULT': {
      // As LOAD_STATE, but with the result in hand — and the saved record
      // kept, so a logged-in user's re-estimate goes to the same record.
      const p = action.payload;
      return {
        ...initialState,
        currentStep: p.currentStep,
        completedSteps: [...p.completedSteps],
        calculationMode: p.calculationMode,
        projectInfo: { ...p.projectInfo },
        wallsConfig: { ...p.wallsConfig },
        ceilingConfig: { ...p.ceilingConfig },
        dimensionsConfig: { ...p.dimensionsConfig },
        applicationConfig: { ...p.applicationConfig },
        individualWalls: undefined,
        currency: p.currency,
        estimateResult: state.savedEstimate ? { ...action.result, id: state.savedEstimate.guid } : action.result,
        isCalculating: false,
        calculationError: null,
        savedEstimate: state.savedEstimate,
      };
    }

    case 'SET_PREVIEW_IMAGE':
      if (!state.estimateResult?.preview) return state;
      return { ...state, estimateResult: { ...state.estimateResult, preview: { ...state.estimateResult.preview, image: action.image } } };

    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

const EstimatorContext = createContext<EstimatorContextValue | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface EstimatorProviderProps {
  children: ReactNode;
}

export const EstimatorProvider: React.FC<EstimatorProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(estimatorReducer, initialState);

  // Tiered currency detection
  const { currency: detectedCurrency, setCurrency: setDetectedCurrency, isDetecting: isCurrencyDetecting } = useCurrency();
  const currencyInitialized = useRef(false);

  // Sync detected currency into reducer state on first detection
  useEffect(() => {
    if (!isCurrencyDetecting && !currencyInitialized.current) {
      currencyInitialized.current = true;
      dispatch({ type: 'SET_CURRENCY', currency: detectedCurrency });
    }
  }, [isCurrencyDetecting, detectedCurrency]);

  // Track whether we just loaded state and need to auto-calculate
  const pendingAutoCalculateRef = React.useRef(false);

  // Register a callback so the data manager can push loaded state into React
  useEffect(() => {
    registerStateLoader((payload: LoadStatePayload, autoCalculate: boolean) => {
      dispatch({ type: 'LOAD_STATE', payload });
      if (autoCalculate) {
        pendingAutoCalculateRef.current = true;
      }
    });

    // Rehydrate from sessionStorage if the user refreshed mid-wizard.
    const snapshot = loadSessionSnapshot();
    if (snapshot) {
      dispatch({ type: 'LOAD_STATE', payload: snapshot.payload });
      if (snapshot.autoCalculate) {
        pendingAutoCalculateRef.current = true;
      }
    }
  }, []);

  // After LOAD_STATE renders, auto-calculate if flagged
  useEffect(() => {
    if (pendingAutoCalculateRef.current && !state.isCalculating && !state.estimateResult) {
      const stepOrder = STEP_ORDERS[state.calculationMode] || STEP_ORDERS.FullRoom;
      const allComplete = stepOrder.every(s => state.completedSteps.includes(s));
      if (allComplete) {
        pendingAutoCalculateRef.current = false;
        dispatch({ type: 'CALCULATE_START' });
        (async () => {
          try {
            // Rehydrate product data if it changed server-side before computing
            // (online only; no-op offline or when unchanged).
            await maybeRefreshProducts(getLoadedRetailerId() ?? 1);
            const input = buildEstimateInput(state);
            const result = doEstimate(input);
            result.projectInfo = state.projectInfo;
            dispatch({ type: 'CALCULATE_SUCCESS', result });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Calculation failed';
            dispatch({ type: 'CALCULATE_ERROR', error: message });
          }
        })();
      } else {
        pendingAutoCalculateRef.current = false;
      }
    }
  }, [state]);

  // Get step order based on current calculation mode
  const getStepOrder = useCallback((): WizardStep[] => {
    return STEP_ORDERS[state.calculationMode] || STEP_ORDERS.FullRoom;
  }, [state.calculationMode]);

  // Navigation
  const goToStep = useCallback((step: WizardStep) => {
    dispatch({ type: 'SET_STEP', step });
    // Smooth scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const nextStep = useCallback(() => {
    const stepOrder = getStepOrder();
    const currentIndex = stepOrder.indexOf(state.currentStep);
    if (currentIndex < stepOrder.length - 1) {
      dispatch({ type: 'COMPLETE_STEP', step: state.currentStep });
      dispatch({ type: 'SET_STEP', step: stepOrder[currentIndex + 1] });
      // Smooth scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [state.currentStep, getStepOrder]);

  const prevStep = useCallback(() => {
    const stepOrder = getStepOrder();
    const currentIndex = stepOrder.indexOf(state.currentStep);
    if (currentIndex > 0) {
      dispatch({ type: 'SET_STEP', step: stepOrder[currentIndex - 1] });
      // Smooth scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [state.currentStep, getStepOrder]);

  // Mode selection
  const setCalculationMode = useCallback((mode: WizardState['calculationMode']) => {
    dispatch({ type: 'SET_CALCULATION_MODE', mode });
  }, []);

  // Configuration updates
  const updateWallsConfig = useCallback(
    (config: Partial<WizardState['wallsConfig']>) => {
      dispatch({ type: 'UPDATE_WALLS_CONFIG', config });
    },
    []
  );

  const updateCeilingConfig = useCallback(
    (config: Partial<WizardState['ceilingConfig']>) => {
      dispatch({ type: 'UPDATE_CEILING_CONFIG', config });
    },
    []
  );

  const updateDimensionsConfig = useCallback(
    (config: Partial<WizardState['dimensionsConfig']>) => {
      dispatch({ type: 'UPDATE_DIMENSIONS_CONFIG', config });
    },
    []
  );

  const updateApplicationConfig = useCallback(
    (config: Partial<WizardState['applicationConfig']>) => {
      dispatch({ type: 'UPDATE_APPLICATION_CONFIG', config });
    },
    []
  );

  const updateProjectInfo = useCallback(
    (info: Partial<ProjectInfo>) => {
      dispatch({ type: 'UPDATE_PROJECT_INFO', info });
    },
    []
  );

  // Individual walls management
  const setIndividualWalls = useCallback((walls: WallConfig[]) => {
    dispatch({ type: 'SET_INDIVIDUAL_WALLS', walls });
  }, []);

  const addIndividualWall = useCallback(() => {
    dispatch({ type: 'ADD_INDIVIDUAL_WALL' });
  }, []);

  const copyIndividualWall = useCallback((index: number) => {
    dispatch({ type: 'COPY_INDIVIDUAL_WALL', index });
  }, []);

  const updateIndividualWall = useCallback((index: number, wall: Partial<WallConfig>) => {
    dispatch({ type: 'UPDATE_INDIVIDUAL_WALL', index, wall });
  }, []);

  const removeIndividualWall = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_INDIVIDUAL_WALL', index });
  }, []);

  // Ceiling zones management
  const addCeilingZone = useCallback(() => {
    dispatch({ type: 'ADD_CEILING_ZONE' });
  }, []);

  const copyCeilingZone = useCallback((index: number) => {
    const zones = state.ceilingConfig.zones;
    if (!zones || !zones[index]) return;
    const source = zones[index];
    const newZone = {
      ...source,
      id: `zone-${Date.now()}-${zones.length}`,
      name: `${source.name} (Copy)`,
    };
    const updatedZones = [...zones];
    updatedZones.splice(index + 1, 0, newZone);
    dispatch({ type: 'UPDATE_CEILING_CONFIG', config: { zones: updatedZones } });
  }, [state.ceilingConfig.zones]);

  const updateCeilingZone = useCallback((index: number, zone: Partial<CeilingZone>) => {
    dispatch({ type: 'UPDATE_CEILING_ZONE', index, zone });
  }, []);

  const removeCeilingZone = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_CEILING_ZONE', index });
  }, []);

  // Calculate estimate
  const calculateEstimate = useCallback(async () => {
    dispatch({ type: 'CALCULATE_START' });

    try {
      // A new estimate is being generated — pull fresh product data if it has
      // changed server-side (online only; a no-op offline or when unchanged).
      await maybeRefreshProducts(getLoadedRetailerId() ?? 1);

      // Build input from wizard state
      const input = buildEstimateInput(state);
      const result = doEstimate(input);

      // Add project info to result
      result.projectInfo = state.projectInfo;

      dispatch({ type: 'CALCULATE_SUCCESS', result });
      // Smooth scroll to top of page to show results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Calculation failed';
      dispatch({ type: 'CALCULATE_ERROR', error: message });
    }
  }, [state]);

  const updateUserNotes = useCallback((notes: string) => {
    dispatch({ type: 'UPDATE_USER_NOTES', notes });
  }, []);

  const setCurrency = useCallback((currency: Currency) => {
    dispatch({ type: 'SET_CURRENCY', currency });
    setDetectedCurrency(currency); // Persist to session as manual override
  }, [setDetectedCurrency]);

  const clearResults = useCallback(() => {
    dispatch({ type: 'CLEAR_RESULTS' });
  }, []);

  // An estimate made in the 3D designer (Advanced mode). The materials are
  // the plan's (withDesignerMaterials); the wizard state around them — what
  // the config strips, the visualizer payload and a reprice read — is read
  // off the plan (wizardSeedFromPlan).
  const loadDesignerEstimate = useCallback(async ({ doc, preview }: { doc: EstimateDesigner; preview: EstimatePreview }) => {
    await maybeRefreshProducts(getLoadedRetailerId() ?? 1);
    const seed = wizardSeedFromPlan(doc.plan, doc.focusRoomId, state);
    const payload: LoadStatePayload = {
      ...seed,
      projectInfo: state.projectInfo,
      currency: state.currency,
      completedSteps: ['mode', 'walls', 'ceiling', 'dimensions', 'application'],
      currentStep: 'application',
    };
    const input = buildEstimateInput({ ...state, ...payload });
    const result = withDesignerMaterials(doEstimate(input), doc.plan);
    result.projectInfo = state.projectInfo;
    result.designer = doc;
    result.preview = preview;
    trackDesignerEstimate();
    dispatch({ type: 'LOAD_DESIGNER_RESULT', payload, result });
    window.scrollTo({ top: 0 });
  }, [state]);

  const setPreviewImage = useCallback((image: string) => {
    dispatch({ type: 'SET_PREVIEW_IMAGE', image });
  }, []);

  const setSavedEstimate = useCallback((saved: { guid: string; name: string; projectType?: string } | null) => {
    dispatch({ type: 'SET_SAVED_ESTIMATE', saved });
  }, []);

  // Background auto-save: once an estimate is named/saved (logged-in users
  // only), persist the latest config + result to its DB record as the user
  // moves through the wizard. Debounced so rapid edits collapse into one write.
  // estimateResult is omitted while mid-edit so we never null a good output.
  useEffect(() => {
    const saved = state.savedEstimate;
    if (!saved) return;
    const t = setTimeout(() => {
      patchAuthEstimateData(saved.guid, {
        wizardState: buildWizardSnapshot(state),
        estimateData: state.estimateResult ?? undefined,
      }).catch(() => { /* best-effort */ });
    }, 1500);
    return () => clearTimeout(t);
  }, [state]);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    clearSessionSnapshot();
    // Start Over starts Advanced mode over too: the last room does not sit in
    // the floor planner for the next session (Rob, 2026-10-13).
    clearAdvancedState();
  }, []);

  const loadState = useCallback((payload: LoadStatePayload) => {
    dispatch({ type: 'LOAD_STATE', payload });
  }, []);

  // Validation helper functions exposed to components
  const validateSlatWall = useCallback(
    (wallHeight: number, startHeight: number, numPanels: number): number => {
      return validateSlatWallPanels(wallHeight, startHeight, numPanels);
    },
    []
  );

  const validateGarageDoors = useCallback(
    (
      wallLength: number,
      wallHeight: number,
      doorWidth: number,
      doorHeight: number,
      numDoors: number
    ) => {
      return validateGarageDoorConfig(wallLength, wallHeight, doorWidth, doorHeight, numDoors);
    },
    []
  );

  // Computed values
  const canProceed = validateCanProceed(state);

  const stepOrder = getStepOrder();
  const isComplete = state.completedSteps.length === stepOrder.length;

  const value: EstimatorContextValue = {
    state,
    goToStep,
    nextStep,
    prevStep,
    setCalculationMode,
    updateWallsConfig,
    updateCeilingConfig,
    updateDimensionsConfig,
    updateApplicationConfig,
    updateProjectInfo,
    setIndividualWalls,
    addIndividualWall,
    copyIndividualWall,
    updateIndividualWall,
    removeIndividualWall,
    addCeilingZone,
    copyCeilingZone,
    updateCeilingZone,
    removeCeilingZone,
    calculateEstimate,
    updateUserNotes,
    setCurrency,
    clearResults,
    setSavedEstimate,
    reset,
    loadState,
    loadDesignerEstimate,
    setPreviewImage,
    canProceed,
    isComplete,
    getStepOrder,
    getValidationErrors: () => getValidationErrorsForState(state),
    validateSlatWall,
    validateGarageDoors,
  };

  // Sync state to the estimate data manager on every state change
  useEffect(() => {
    syncEstimateData(state);
  }, [state]);

  // Warn the user before an accidental refresh / tab-close would wipe progress.
  // Keep the latest state in a ref so the handler (installed once) always
  // reads fresh data without re-binding the listener on every change.
  const latestStateRef = useRef(state);
  useEffect(() => {
    latestStateRef.current = state;
  }, [state]);
  useEffect(() => {
    const hasMeaningfulProgress = (s: EstimatorState): boolean => {
      if (s.estimateResult) return true;
      if (s.completedSteps.length > 0) return true;
      if (s.currentStep !== 'mode') return true;
      // User typed something into project info (e.g. started lead capture)
      const p = s.projectInfo;
      if (p.projectName || p.customerName || p.email || p.phone || p.location) return true;
      return false;
    };

    const handler = (event: BeforeUnloadEvent) => {
      if (!hasMeaningfulProgress(latestStateRef.current)) return;
      // Modern browsers show a generic "Leave site?" prompt; the message
      // itself is ignored, but preventDefault + returnValue triggers it.
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  return <EstimatorContext.Provider value={value}>{children}</EstimatorContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export function useEstimator(): EstimatorContextValue {
  const context = useContext(EstimatorContext);
  if (!context) {
    throw new Error('useEstimator must be used within an EstimatorProvider');
  }
  return context;
}

// ============================================================================
// Helpers
// ============================================================================

import { ROOM_PRESETS, GARAGE_DOOR_PRESETS, calculateSlatWallFromCoverage } from '../data/presets';

export interface ValidationError {
  fieldId: string;
  message: string;
}

function getValidationErrorsForState(state: EstimatorState): ValidationError[] {
  const errors: ValidationError[] = [];

  switch (state.currentStep) {
    case 'mode':
      break;

    case 'walls':
      if (state.calculationMode === 'IndividualWalls') {
        const walls = state.individualWalls;
        if (!walls || walls.length === 0) {
          errors.push({ fieldId: 'add-wall', message: tr('wizard:validation.walls.addWall') });
          break;
        }
        for (let i = 0; i < walls.length; i++) {
          const wall = walls[i];
          // The wall's own name when it has one, otherwise the generated
          // "Wall 3" — which is display text and so is itself translated.
          const label = wall.name || tr('wizard:walls.fallbackName', { number: i + 1 });
          if (!wall.name || wall.name.trim() === '')
            errors.push({ fieldId: `wall-${i}-name`, message: tr('wizard:validation.walls.nameRequired', { label }) });
          if (wall.length < 1 || wall.length > 200)
            errors.push({ fieldId: `wall-${i}-length`, message: tr('wizard:validation.walls.lengthRange', { label }) });
          if (wall.height < 1 || wall.height > 20)
            errors.push({ fieldId: `wall-${i}-height`, message: tr('wizard:validation.walls.heightRange', { label }) });
          if (wall.slatWall?.enabled) {
            if (wall.slatWall.numPanelRows < 1)
              errors.push({ fieldId: `wall-${i}-slatwall-panels`, message: tr('wizard:validation.walls.slatWallMinPanels', { label }) });
            if (wall.slatWall.startHeight < 0)
              errors.push({ fieldId: `wall-${i}-slatwall-startHeight`, message: tr('wizard:validation.walls.slatWallNegativeStart', { label }) });
            const slatTop = wall.slatWall.startHeight + (wall.slatWall.numPanelRows * 0.5);
            if (slatTop > wall.height)
              errors.push({ fieldId: `wall-${i}-slatwall-panels`, message: tr('wizard:validation.walls.slatWallExceedsHeight', { label }) });
          }
          if (wall.garage?.enabled) {
            if (wall.garage.width < 4 || wall.garage.width > wall.length)
              errors.push({ fieldId: `wall-${i}-garage-width`, message: tr('wizard:validation.walls.doorWidthRange', { label, max: wall.length }) });
            if (wall.garage.height < 6 || wall.garage.height > wall.height)
              errors.push({ fieldId: `wall-${i}-garage-height`, message: tr('wizard:validation.walls.doorHeightRange', { label, max: wall.height }) });
            if (wall.garage.numDoors < 1)
              errors.push({ fieldId: `wall-${i}-garage-doors`, message: tr('wizard:validation.walls.doorCount', { label }) });
            if (wall.garage.width * wall.garage.numDoors > wall.length)
              errors.push({ fieldId: `wall-${i}-garage-doors`, message: tr('wizard:validation.walls.doorTotalWidth', { label }) });
          }
        }
      } else {
        if (state.wallsConfig.usePresets && !state.wallsConfig.selectedPresetId) {
          errors.push({ fieldId: 'design-presets', message: tr('wizard:validation.walls.selectPreset') });
        }
        // Check if SlatWall toggle is on (via slatWallEnabled flag or panels > 0)
        const slatWallOn = state.wallsConfig.slatWallEnabled ?? state.wallsConfig.slatWallNumPanels > 0;
        if (!state.wallsConfig.usePresets && slatWallOn) {
          if (state.wallsConfig.slatWallNumPanels < 1)
            errors.push({ fieldId: 'slatwall-panels', message: tr('wizard:validation.walls.slatWallMinPanelsSimple') });
        }
      }
      break;

    case 'ceiling':
      if (state.calculationMode === 'CeilingOnly') {
        if (!state.ceilingConfig.zones || state.ceilingConfig.zones.length === 0) {
          errors.push({ fieldId: 'ceiling-zones', message: tr('wizard:validation.ceiling.addZone') });
        } else {
          for (let i = 0; i < state.ceilingConfig.zones.length; i++) {
            const zone = state.ceilingConfig.zones[i];
            const label = zone.name || tr('wizard:ceiling.fallbackName', { number: i + 1 });
            if (!zone.name || zone.name.trim() === '')
              errors.push({ fieldId: `zone-${i}-name`, message: tr('wizard:validation.ceiling.nameRequired', { label }) });
            if (zone.length < 1 || zone.length > 200)
              errors.push({ fieldId: `zone-${i}-length`, message: tr('wizard:validation.ceiling.lengthRange', { label }) });
            if (zone.width < 1 || zone.width > 200)
              errors.push({ fieldId: `zone-${i}-width`, message: tr('wizard:validation.ceiling.widthRange', { label }) });
          }
        }
      }
      // IndividualWalls ceiling validation
      if (state.calculationMode === 'IndividualWalls' && state.ceilingConfig.include) {
        const zones = state.ceilingConfig.zones;
        if (zones) {
          for (let i = 0; i < zones.length; i++) {
            const zone = zones[i];
            const label = zone.name || tr('wizard:ceiling.fallbackName', { number: i + 1 });
            if (!zone.name || zone.name.trim() === '')
              errors.push({ fieldId: `zone-${i}-name`, message: tr('wizard:validation.ceiling.nameRequired', { label }) });
            if (zone.length < 1 || zone.length > 200)
              errors.push({ fieldId: `zone-${i}-length`, message: tr('wizard:validation.ceiling.lengthRange', { label }) });
            if (zone.width < 1 || zone.width > 200)
              errors.push({ fieldId: `zone-${i}-width`, message: tr('wizard:validation.ceiling.widthRange', { label }) });
          }
        }
      }
      break;

    case 'dimensions': {
      if (!state.dimensionsConfig.usePreset && state.dimensionsConfig.customDimensions) {
        const { length, width, height } = state.dimensionsConfig.customDimensions;
        if (length < 1 || length > 200)
          errors.push({ fieldId: 'custom-length', message: tr('wizard:dimensions.errors.length') });
        if (width < 1 || width > 200)
          errors.push({ fieldId: 'custom-width', message: tr('wizard:dimensions.errors.width') });
        if (state.calculationMode !== 'CeilingOnly' && (height < 2 || height > 20))
          errors.push({ fieldId: 'custom-height', message: tr('wizard:dimensions.errors.height') });
      }
      if (state.wallsConfig.slatWallNumPanels > 0) {
        const wallHeight = state.dimensionsConfig.customDimensions?.height
          || ROOM_PRESETS.find(p => p.id === state.dimensionsConfig.presetId)?.height
          || 10;
        const slatWallTop = state.wallsConfig.slatWallStartHeight + (state.wallsConfig.slatWallNumPanels * 0.5);
        if (slatWallTop > wallHeight)
          errors.push({ fieldId: 'slatwall-panels', message: tr('wizard:validation.dimensions.slatWallExceedsHeight') });
      }
      break;
    }

    case 'application': {
      if (state.applicationConfig.garage?.enabled) {
        let roomDims = { length: 20, width: 12, height: 10 };
        if (state.dimensionsConfig.usePreset && state.dimensionsConfig.presetId) {
          const preset = ROOM_PRESETS.find(p => p.id === state.dimensionsConfig.presetId);
          if (preset) roomDims = { length: preset.length, width: preset.width, height: preset.height };
        } else if (state.dimensionsConfig.customDimensions) {
          roomDims = state.dimensionsConfig.customDimensions;
        }
        const wallLength = state.applicationConfig.garage.onWallType === 'LengthWall' ? roomDims.length : roomDims.width;
        if (state.applicationConfig.garage.width < 1 || state.applicationConfig.garage.width > wallLength)
          errors.push({ fieldId: 'garage-width', message: tr('wizard:validation.application.doorWidthRange', { max: wallLength }) });
        if (state.applicationConfig.garage.height < 1 || state.applicationConfig.garage.height > roomDims.height)
          errors.push({ fieldId: 'garage-height', message: tr('wizard:validation.application.doorHeightRange', { max: roomDims.height }) });
        if (state.applicationConfig.garage.width * state.applicationConfig.garage.numDoors > wallLength)
          errors.push({ fieldId: 'garage-doors', message: tr('wizard:validation.application.doorTotalWidth') });
      }
      break;
    }

    default:
      break;
  }

  return errors;
}

function validateCanProceed(state: EstimatorState): boolean {
  switch (state.currentStep) {
    case 'mode':
      return true;
    case 'walls':
      if (state.calculationMode === 'IndividualWalls') {
        const walls = state.individualWalls;
        if (!walls || walls.length === 0) return false;
        // Validate every wall
        for (const wall of walls) {
          // Name required
          if (!wall.name || wall.name.trim() === '') return false;
          // Basic dimensions
          if (wall.length < 1 || wall.length > 200) return false;
          if (wall.height < 1 || wall.height > 20) return false;
          // SlatWall: if enabled, must have at least 1 panel and valid start height
          if (wall.slatWall?.enabled) {
            if (wall.slatWall.numPanelRows < 1) return false;
            if (wall.slatWall.startHeight < 0) return false;
            const slatTop = wall.slatWall.startHeight + (wall.slatWall.numPanelRows * 0.5);
            if (slatTop > wall.height) return false;
          }
          // Garage: if enabled, validate door fits
          if (wall.garage?.enabled) {
            if (wall.garage.width < 4 || wall.garage.width > wall.length) return false;
            if (wall.garage.height < 6 || wall.garage.height > wall.height) return false;
            if (wall.garage.numDoors < 1) return false;
            if (wall.garage.width * wall.garage.numDoors > wall.length) return false;
          }
        }
        return true;
      }
      // FullRoom: if using design presets, require one to be selected
      if (state.wallsConfig.usePresets) {
        return !!state.wallsConfig.selectedPresetId;
      }
      // FullRoom custom: if slatwall toggle is on, must have at least 1 panel
      const slatOn = state.wallsConfig.slatWallEnabled ?? state.wallsConfig.slatWallNumPanels > 0;
      if (slatOn && state.wallsConfig.slatWallNumPanels < 1) return false;
      return true;
    case 'ceiling':
      if (state.calculationMode === 'CeilingOnly') {
        if ((state.ceilingConfig.zones?.length ?? 0) === 0) return false;
        for (const zone of (state.ceilingConfig.zones || [])) {
          if (!zone.name || zone.name.trim() === '') return false;
          if (zone.length < 1 || zone.length > 200) return false;
          if (zone.width < 1 || zone.width > 200) return false;
        }
        return true;
      }
      if (state.calculationMode === 'IndividualWalls' && state.ceilingConfig.include) {
        for (const zone of (state.ceilingConfig.zones || [])) {
          if (!zone.name || zone.name.trim() === '') return false;
          if (zone.length < 1 || zone.length > 200) return false;
          if (zone.width < 1 || zone.width > 200) return false;
        }
      }
      return true;
    case 'dimensions': {
      // Validate custom dimensions if not using preset
      if (!state.dimensionsConfig.usePreset && state.dimensionsConfig.customDimensions) {
        const { length, width, height } = state.dimensionsConfig.customDimensions;
        if (length < 1 || length > 200 || width < 1 || width > 200) return false;
        if (state.calculationMode !== 'CeilingOnly' && (height < 2 || height > 20)) return false;
      }
      // Check for slatwall conflict
      if (state.wallsConfig.slatWallNumPanels > 0) {
        const wallHeight = state.dimensionsConfig.customDimensions?.height
          || ROOM_PRESETS.find(p => p.id === state.dimensionsConfig.presetId)?.height
          || 10;
        const slatWallTop = state.wallsConfig.slatWallStartHeight + (state.wallsConfig.slatWallNumPanels * 0.5);
        if (slatWallTop > wallHeight) return false;
      }
      return true;
    }
    case 'application': {
      // Validate garage door configuration if enabled
      if (state.applicationConfig.garage.enabled && state.applicationConfig.garage.onWallType !== 'None') {
        // Get room dimensions
        let roomDims = { length: 20, width: 12, height: 10 };
        if (state.dimensionsConfig.usePreset && state.dimensionsConfig.presetId) {
          const preset = ROOM_PRESETS.find(p => p.id === state.dimensionsConfig.presetId);
          if (preset) {
            roomDims = { length: preset.length, width: preset.width, height: preset.height };
          }
        } else if (state.dimensionsConfig.customDimensions) {
          roomDims = state.dimensionsConfig.customDimensions;
        }

        const { width, height, numDoors, onWallType } = state.applicationConfig.garage;
        const wallLength = onWallType === 'LengthWall' ? roomDims.length : roomDims.width;

        // Check validations
        if (width < 1 || width > wallLength) return false;
        if (height < 1 || height > roomDims.height) return false;
        if (numDoors < 1) return false;
        if (width * numDoors > wallLength) return false;
      }
      return true;
    }
    case 'leadCapture': {
      // Require project name and valid email
      const email = state.projectInfo.email.trim();
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      return state.projectInfo.projectName.trim() !== '' && isValidEmail;
    }
    default:
      return true;
  }
}

function buildEstimateInput(state: EstimatorState): EstimateInput {
  // Get room dimensions
  let roomDimensions = { length: 20, width: 12, height: 10 };

  if (state.calculationMode === 'CeilingOnly' && state.ceilingConfig.zones?.length) {
    // For ceiling only, use the first zone dimensions as room dimensions
    const firstZone = state.ceilingConfig.zones[0];
    roomDimensions = {
      length: firstZone.length,
      width: firstZone.width,
      height: 10, // Default height (not used for ceiling-only)
    };
  } else if (state.dimensionsConfig.usePreset && state.dimensionsConfig.presetId) {
    const preset = ROOM_PRESETS.find(p => p.id === state.dimensionsConfig.presetId);
    if (preset) {
      roomDimensions = {
        length: preset.length,
        width: preset.width,
        height: preset.height,
      };
    }
  } else if (state.dimensionsConfig.customDimensions) {
    roomDimensions = state.dimensionsConfig.customDimensions;
  }

  // For CeilingOnly mode, ceiling is always included
  const includeCeiling = state.calculationMode === 'CeilingOnly' ? true : state.ceilingConfig.include;

  // For FullRoom mode, ceiling uses room dimensions (not zones)
  // For CeilingOnly and IndividualWalls modes, ceiling uses zones
  const shouldUseZones = state.calculationMode !== 'FullRoom';

  const input: EstimateInput = {
    calculationMode: state.calculationMode,
    roomDimensions,
    includeCeiling,
    ceilingConfig: {
      enabled: includeCeiling,
      color: state.ceilingConfig.color,
    },
    ceilingZones: shouldUseZones ? state.ceilingConfig.zones : undefined,
  };

  if (state.calculationMode === 'FullRoom') {
    // Use direct number of panels instead of percentage
    const numPanelRows = state.wallsConfig.slatWallNumPanels;

    input.fullRoomWallConfig = {
      wallColor: state.wallsConfig.color,
      wallOrientation: state.wallsConfig.orientation,
      slatWall: {
        enabled: numPanelRows > 0,
        startHeight: state.wallsConfig.slatWallStartHeight,
        numPanelRows,
        color: state.wallsConfig.slatWallColor,
        useCapTrim: numPanelRows > 0,
      },
      garage: state.applicationConfig.garage,
    };
  } else if (state.calculationMode === 'IndividualWalls' && state.individualWalls) {
    input.individualWalls = state.individualWalls;
  }

  return input;
}
